import { ref } from 'vue';
import * as faceapi from 'face-api.js';
import useConfig from '@/composables/useConfig';
import { SYSTEM_VARIABLES_MAP, VARIABLE_VALUE_TYPES } from '@/js/constants';

const isActive = ref(false);
const isHumanDetected = ref(false);
const MODEL_PATH = `${process.env.NODE_ENV === 'development' ? `${process.env.BASE_URL}/` : ''}face-detection-models`;
let faceDetectionStreams = [];

export default function useFaceDetection() {
  let videoEl;
  let mediaStream;
  let interval = null;

  async function checkForHuman() {
    const { currentConfig, setVariables, resetVariables } = useConfig();

    const options = currentConfig.value.customPayload.useFaceDetectionSSD
      ? new faceapi.SsdMobilenetv1Options({
        minConfidence: currentConfig.value.customPayload.faceConfidence || 0.5,
      })
      : new faceapi.TinyFaceDetectorOptions({
        scoreThreshold: currentConfig.value.customPayload.faceConfidence || 0.5,
      });

    const detection = await (currentConfig.value.ageGenderDetectionActive
      ? faceapi.detectSingleFace(videoEl, options).withAgeAndGender()
      : faceapi.detectSingleFace(videoEl, options)
    );

    if (detection && currentConfig.value.ageGenderDetectionActive) {
      setVariables([
        {
          id: SYSTEM_VARIABLES_MAP.AGE.id,
          value: { value: Math.round(detection.age), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
        },
        { id: SYSTEM_VARIABLES_MAP.GENDER.id, value: { value: detection.gender === 'male' ? 'Male' : 'Female', type: VARIABLE_VALUE_TYPES.PRIMITIVE } },
      ]);
    } else {
      resetVariables([SYSTEM_VARIABLES_MAP.AGE.id, SYSTEM_VARIABLES_MAP.GENDER.id]);
    }
    isHumanDetected.value = !!detection;
  }

  function disableFD() {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
    if (mediaStream) {
      videoEl.pause();
      const tracks = mediaStream.getVideoTracks();
      tracks.forEach((track) => {
        track.stop();
      });
    }
    faceDetectionStreams.forEach((stream) => {
      const tracks = stream.getVideoTracks();
      tracks.forEach((t) => t.stop());
    });
    faceDetectionStreams = [];
  }

  async function enableFD(init = false) {
    try {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }

      mediaStream = await window.navigator.mediaDevices.getUserMedia({ video: true });
      faceDetectionStreams.push(mediaStream);

      videoEl.srcObject = mediaStream;
      videoEl.onloadedmetadata = async () => {
        videoEl.play();
        if (init) {
          const webcamCanvas = faceapi.createCanvasFromMedia(videoEl);
          faceapi.matchDimensions(webcamCanvas, { width: 640, height: 480 });
        }
        interval = setInterval(checkForHuman, 1000);
        await checkForHuman();
        isActive.value = true;
        return true;
      };
    } catch (e) {
      isHumanDetected.value = true;
    }
    return false;
  }

  async function initFD(videoElement) {
    if (!videoElement) {
      isHumanDetected.value = true;
      return false;
    }

    try {
      const { currentConfig } = useConfig();
      const loadPromises = currentConfig.value.customPayload.useFaceDetectionSSD ? [
        faceapi.nets.ssdMobilenetv1.load(MODEL_PATH),
      ] : [
        faceapi.nets.tinyFaceDetector.load(MODEL_PATH),
      ];

      if (currentConfig.value.ageGenderDetectionActive) {
        loadPromises.push(faceapi.nets.ageGenderNet.load(MODEL_PATH));
      }

      await Promise.all(loadPromises);
    } catch (err) {
      isHumanDetected.value = true;
      return false;
    }

    videoEl = videoElement;

    await enableFD(true);
    return true;
  }

  function isFDActive() {
    return isActive.value;
  }

  return {
    isHumanDetected,
    initFD,
    checkForHuman,
    disableFD,
    enableFD,
    isFDActive,
  };
}
